import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Divider,
  FormControlLabel, MenuItem, Select, Slider, Stack, Switch, TextField, Typography,
} from '@mui/material';
import ScreenshotMonitorIcon from '@mui/icons-material/ScreenshotMonitor';
import HistoryIcon from '@mui/icons-material/History';
import { getActivityPolicy, updateActivityPolicy } from '../api/client';
import InfoTooltip from '../components/InfoTooltip';
import { useUnsavedChangesGuard } from '../hooks/useUnsavedChanges';

/**
 * Vendor Admin: Work Activity Monitoring policy — screenshot capture and
 * browser-history collection settings. Supervisors never reach this page
 * (not linked from their nav) and the API itself rejects writes from any
 * non-vendor-admin role, same defense-in-depth pattern as AlarmConfigPage.
 */
export default function ActivityPolicyPage() {
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  const load = () => {
    setLoading(true);
    getActivityPolicy().then(setPolicy).catch(e => setError(e.message)).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const set = (field) => (value) => {
    setPolicy(prev => ({ ...prev, [field]: value }));
    setDirty(true);
    setSaved(false);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const patch = {
        screenshot_enabled: policy.screenshot_enabled,
        screenshot_interval_mode: policy.screenshot_interval_mode,
        screenshot_interval_minutes: Number(policy.screenshot_interval_minutes),
        screenshot_interval_max_minutes: policy.screenshot_interval_mode === 'random'
          ? Number(policy.screenshot_interval_max_minutes) : null,
        screenshot_quality: Number(policy.screenshot_quality),
        browser_history_enabled: policy.browser_history_enabled,
        browser_sync_interval_minutes: Number(policy.browser_sync_interval_minutes),
        retention_days: Number(policy.retention_days),
      };
      const updated = await updateActivityPolicy(patch);
      setPolicy(updated);
      setDirty(false);
      setSaved(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const discard = () => { load(); setDirty(false); };
  const { confirmNavigation } = useUnsavedChangesGuard(dirty, { onSave: save, onDiscard: discard });
  const handleCancel = () => confirmNavigation(discard);

  if (loading || !policy) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto', p: 3 }}>
      <Typography variant="h5" sx={{ mb: 0.5 }}>Work Activity Monitoring</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configure automatic screenshot capture and browser-history collection for this vendor's devices.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {saved && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaved(false)}>Policy saved and pushed to online devices.</Alert>}

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <ScreenshotMonitorIcon color="primary" />
            <Typography variant="h6">Screenshot Capture</Typography>
          </Stack>
          <FormControlLabel
            control={<Switch checked={!!policy.screenshot_enabled} onChange={e => set('screenshot_enabled')(e.target.checked)} />}
            label="Enable automatic screenshot capture"
          />
          <Divider sx={{ my: 2 }} />
          <Stack spacing={2} sx={{ opacity: policy.screenshot_enabled ? 1 : 0.5 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography sx={{ minWidth: 120 }}>Interval mode</Typography>
              <Select
                size="small" value={policy.screenshot_interval_mode || 'fixed'}
                onChange={e => set('screenshot_interval_mode')(e.target.value)}
                disabled={!policy.screenshot_enabled}
              >
                <MenuItem value="fixed">Fixed</MenuItem>
                <MenuItem value="random">Random</MenuItem>
              </Select>
              <InfoTooltip
                description="Randomizes capture timing inside the configured interval."
                saveRequired scope="future"
              />
            </Stack>

            <Typography variant="caption" color="text.secondary">
              Capture Interval
              <InfoTooltip description="Automatic screenshot frequency." saveRequired scope="future" />
            </Typography>
            {policy.screenshot_interval_mode === 'random' ? (
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Minimum (minutes)" type="number" size="small"
                  value={policy.screenshot_interval_minutes ?? 20}
                  onChange={e => set('screenshot_interval_minutes')(e.target.value)}
                  disabled={!policy.screenshot_enabled}
                />
                <TextField
                  label="Maximum (minutes)" type="number" size="small"
                  value={policy.screenshot_interval_max_minutes ?? 60}
                  onChange={e => set('screenshot_interval_max_minutes')(e.target.value)}
                  disabled={!policy.screenshot_enabled}
                />
              </Stack>
            ) : (
              <TextField
                label="Interval (minutes)" type="number" size="small" sx={{ maxWidth: 220 }}
                value={policy.screenshot_interval_minutes ?? 30}
                onChange={e => set('screenshot_interval_minutes')(e.target.value)}
                disabled={!policy.screenshot_enabled}
              />
            )}

            <Box sx={{ maxWidth: 360 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Image quality: {policy.screenshot_quality ?? 70}%
              </Typography>
              <Slider
                value={policy.screenshot_quality ?? 70} min={10} max={100} step={5}
                onChange={(_, v) => set('screenshot_quality')(v)}
                disabled={!policy.screenshot_enabled}
              />
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <HistoryIcon color="primary" />
            <Typography variant="h6">Browser History Collection</Typography>
          </Stack>
          <FormControlLabel
            control={<Switch checked={!!policy.browser_history_enabled} onChange={e => set('browser_history_enabled')(e.target.checked)} />}
            label="Enable automatic browser-history collection"
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4.5, mb: 2 }}>
            Collects today's history from Chrome, Microsoft Edge, and Brave.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Collection Interval
            <InfoTooltip description="How often browser history is collected." saveRequired scope="future" />
          </Typography>
          <TextField
            label="Sync interval (minutes)" type="number" size="small" sx={{ maxWidth: 220, mt: 0.5 }}
            value={policy.browser_sync_interval_minutes ?? 60}
            onChange={e => set('browser_sync_interval_minutes')(e.target.value)}
            disabled={!policy.browser_history_enabled}
          />
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Retention
            <InfoTooltip
              description="Data older than this will be automatically removed."
              scope="existing" saveRequired
            />
          </Typography>
          <TextField
            label="Keep captured data for (days)" type="number" size="small" sx={{ maxWidth: 220 }}
            value={policy.retention_days ?? 30}
            onChange={e => set('retention_days')(e.target.value)}
          />
        </CardContent>
      </Card>

      <Stack direction="row" spacing={2} alignItems="center">
        {dirty && <Chip size="small" color="warning" variant="outlined" label="Unsaved Changes" />}
        <Button onClick={handleCancel} disabled={!dirty || saving}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={!dirty || saving}>
          {saving ? 'Saving...' : 'Save Policy'}
        </Button>
        <Typography variant="caption" color="text.secondary">
          Version {policy.config_version ?? 0} — changes push to online devices immediately.
        </Typography>
      </Stack>
    </Box>
  );
}
